import React, {useEffect} from 'react'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import questionsApi from '../../services/api/questionLibrary/questionsApi'
import SubmissionBlocker from './submissionBlocker'
import WelcomeScreen from './WelcomeScreen'
import Questions from './Questions'
import {CircularProgress, createTheme, ThemeProvider} from '@mui/material'
import {Experimental_CssVarsProvider as CssVarsProvider} from '@mui/material/styles'
import Mode from './Mode'
import {getTheming} from '../../utils/utilities'
import {Config} from '../../utils/Config'
import {logo, logoCircle} from '../../components/assets/images'
import {mergeSurveyItems} from '../../utils/surveyItems'

export default function SurveyView() {
  const {aud, token} = useParams()
  const [surveyStart, setSurveyStart] = React.useState(false)
  const [surveyEnd, setSurveyEnd] = React.useState(false)
  const [surveyData, setSurveyData] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [language, setLanguage] = React.useState('en')
  const [sourcelanguage, setSourceLanguage] = React.useState('en')
  const [headerImageUrl, setHeaderImageUrl] = React.useState(null)
  const [footerImageUrl, setFooterImageUrl] = React.useState(null)
  const [backgroundColor, setBackgroundColor] = React.useState(null)
  const [ttsEnabled, setTtsEnabled] = React.useState(false)
  const [voice, setVoice] = React.useState(undefined)
  const [coreTheme, setCoreTheme] = React.useState(null)
  const surveyItems = React.useMemo(
    () => mergeSurveyItems(surveyData?.items, surveyData?.questions),
    [surveyData]
  )
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const kioskMode = searchParams.get('kiosk')
  const kioskSchoolSlug = searchParams.get('schoolSlug')

  const [theme, setTheme] = React.useState(
    createTheme({
      components: {
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(0,0,0,0.10)',
              margin: '5px 0',
            },
            label: {
              color: '#fff',
            },
          },
        },

        MuiRadio: {
          styleOverrides: {
            root: {
              color: '#007A7A',
              '&.Mui-checked': {
                color: '#007A7A',
              },
            },
          },
        },
      },
    })
  )

  useEffect(() => {
    const fetchSurveyData = async () => {
      try {
        await loadSurvey()
      } catch (error) {
        console.error('Error loading survey data:', error)
      }
    }

    fetchSurveyData()
  }, [])
  const [lightDarkMode, setLightDarkMode] = React.useState('light')

  useEffect(() => {
    loadSurvey()
  }, [language])

  const loadSurvey = async () => {
    questionsApi
      .getQuestionForParticipants(token, language)
      .then(res => {
        setSurveyData(res)
        theming(res?.surveyTheme)
      })
      .finally(() => setLoading(false))
  }
  const theming = theme => {
    var tt = getTheming(theme)
    if (theme) {
      if (theme.headerImageUrl) {
        setHeaderImageUrl(theme.headerImageUrl)
      }
      if (theme.footerImageUrl) {
        setFooterImageUrl(theme.footerImageUrl)
      }
    }
    if (!theme || !theme.backgroundColor) {
      setBackgroundColor('#F0FBFF')
    } else {
      setBackgroundColor(theme.backgroundColor)
    }
    // const body = document.querySelector('body')
    // body.style.backgroundColor =
    //   lightDarkMode === 'dark' ? '#121212' : theme?.backgroundColor || '#F0FBFF'
    setTheme(tt)
    setCoreTheme(theme)
  }
  useEffect(() => {
    const body = document.querySelector('body')
    if (lightDarkMode === 'dark') {
      body.style.backgroundColor = '#121212'
    } else {
      const bgColor =
        coreTheme?.backgroundColor?.replace('##', '#') ||
        backgroundColor ||
        '#F0FBFF'
      body.style.backgroundColor = bgColor
    }

    // Cleanup function to reset background when component unmounts
    return () => {
      body.style.backgroundColor = ''
    }
  }, [lightDarkMode, coreTheme, backgroundColor])

  // When used from kiosk (kiosk param present), after survey end redirect
  // back into kiosk flow (thank-you screens handle looping/welcome).
  useEffect(() => {
    if (!kioskMode) return
    if (!surveyEnd) return

    if (kioskMode === 'parent') {
      navigate(`/kiosk/${kioskSchoolSlug}/parent/thank-you`, {replace: true})
    } else if (kioskMode === 'student') {
      navigate(`/kiosk/${kioskSchoolSlug}/student/thank-you`, {replace: true})
    }
  }, [kioskMode, kioskSchoolSlug, surveyEnd, navigate])

  return (
    <>
      <div className="survey-header-logo">
        {headerImageUrl && (
          <img
            src={`${Config.BASE_URL}/Artifact/downloadlogo?fileName=${headerImageUrl}`}
            alt="Survey Header"
          />
        )}
      </div>
      <CssVarsProvider>
        <Mode setLightDarkMode={setLightDarkMode} />
        <ThemeProvider
          theme={createTheme(
            lightDarkMode === 'light'
              ? theme
              : {
                  palette: {
                    mode: 'dark',
                    primary: {
                      main: '#000',
                    },
                    secondary: {
                      main: '#555',
                    },
                  },

                  components: {
                    MuiButton: {
                      styleOverrides: {
                        root: {
                          backgroundColor: '#555',
                          color: '#fff',
                          borderColor: '#555',
                          ':hover': {
                            backgroundColor: '#555',
                          },
                          '&.MuiButton-outlined': {
                            backgroundColor: 'transparent',
                            borderColor: '#555',
                            color: '#fff',
                            ':hover': {
                              borderColor: '#555',
                              color: '#fff',
                            },
                          },
                        },
                      },
                    },
                    MuiTextField: {
                      styleOverrides: {
                        root: {
                          '& .MuiInputBase-root': {
                            color: '#fff',                            
                          },                            
                          '& .MuiFormLabel-root': {
                            color: '#fff',
                          },
                          '& .MuiInputBase-input': {
                            color: '#fff',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#fff',
                          },
                          '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#fff',
                          },
                        },
                      },
                    },
                    MuiSlider: {
                      styleOverrides: {
                        root: {
                          color: '#fff',
                          '& .MuiGrid-root': {
                            color: '#fff',                            
                          },
                          '& .MuiSlider-thumb': {
                            backgroundColor: '#007A7A',
                            color: '#007A7A',
                          },                          
                          '& .MuiSlider-rail': {
                            backgroundColor: '#F2F2F2',
                            color: '#007A7A',
                          },                                                   
                        },
                      },
                    },

                    MuiRadio: {                      
                      styleOverrides: {
                        root: {                          
                          borderColor: '#007A7A',
                          color: '#007A7A',
                          '&.Mui-checked': {
                            color: '#007A7A',
                          },
                        },
                      },
                    },

                    MuiCheckbox: {
                      styleOverrides: {
                        root: {
                          borderColor: '#007A7A',
                          color: '#007A7A',
                          '&.Mui-checked': {
                            color: '#007A7A',
                          },
                        },
                      },
                    },

                    MuiPaper: {
                      styleOverrides: {
                        root: {
                          boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)',
                        },
                      },
                    },
                  },
                }
          )}
        >
          {loading ? (
            <div className="welcome">
              <CircularProgress color="inherit" />
            </div>
          ) : surveyData?.isSurveyClosed ? (
            <SubmissionBlocker
              type="closed"
              language={language}
              sourcelanguage={sourcelanguage}
            />
          ) : surveyData?.isSubmitted ? (
            <SubmissionBlocker
              language={language}
              sourcelanguage={sourcelanguage}
            />
          ) : !surveyStart ? (
            <WelcomeScreen
              //theme={surveyData?.surveyTheme}
              setSurveyStart={setSurveyStart}
              surveyData={surveyData}
              language={language}
              lightDarkMode={lightDarkMode}
              setLanguage={setLanguage}
              sourcelanguage={sourcelanguage}
              setSourceLanguage={setSourceLanguage}
              setTtsEnabled={setTtsEnabled}
              setVoice={setVoice}
            />
          ) : surveyEnd ? (
            <SubmissionBlocker
              type="end"
              language={language}
              sourcelanguage={sourcelanguage}
            />
          ) : (
            <Questions
              language={language}
              questions={surveyItems}
              tab={parseInt(aud)}
              setSurveyEnd={setSurveyEnd}
              surveyData={surveyData}
              ttsEnabled={ttsEnabled}
              voice={voice}
            />
          )}
          {/* <img
            src={`${Config.BASE_URL}/Artifact/download?fileName=${footerImageUrl}`}
            style={{width: '10%', height: 'auto', borderRadius: 8}}
          /> */}
        </ThemeProvider>
      </CssVarsProvider>
      <div className="survey-footer-logo">
        {footerImageUrl && (
          <img
            src={`${Config.BASE_URL}/Artifact/downloadlogo?fileName=${footerImageUrl}`}
            alt="Survey Footer"
          />
        )}
      </div>
    </>
  )
}
